/* 
Copyright (c) Karsten Jeske and John Firth
This source code is licensed under the proprietary license found in the
LICENSE file in the root directory of this source tree.
*/

import React, { useEffect, useState } from "react"

/**
 * A recovery the toast offers the user, rendered as a button beside the message.
 *
 * Present only where the failure has a single obvious next step that the app can take on the
 * user's behalf - see the connectivity branch in UserParametersForm (#991). A toast whose
 * message just asks the user to change something needs no action.
 */
export interface ToastAction {
  readonly label: string
  readonly onAction: () => void
}

// Create a custom event for showing toasts
export interface ToastEvent {
  readonly title: string
  readonly message: string
  readonly type: "error" | "success" | "info"
  readonly action?: ToastAction
}

// Create a custom event for showing toasts
export const showToast = (toast: ToastEvent) => {
  const event = new CustomEvent("app-toast", { detail: toast })
  window.dispatchEvent(event)
}

/**
 * Closes an error toast, and does nothing to a success or info one, or when nothing is on
 * screen.
 *
 * The counterpart to `showToast`. An error describes an attempt that failed, so the code
 * starting a *new* attempt is the code that knows the old message is spent - and a pinned one
 * has no other way to go.
 */
// AIDEV-NOTE: (#991) Added because pinning a toast that carries an action (see the timer
// branch below) removed the only thing that used to clear a stale one: the five-second
// deadline. A connection toast would then outlive a later *successful* calculation, which
// raises no toast of its own to replace it, leaving "Try again" sitting over a working page.
//
// AIDEV-NOTE: (#991 review) Scoped by *type*, after two rounds of getting the criterion wrong.
// Dismissing every toast wiped an unrelated confirmation: XlsxUpload shows "Parameters
// successfully loaded from <file>" and then triggers a calculation in its next effect
// (XlsxUpload.tsx, the `needsCalculation` effect), so the dismiss removed it a frame after it
// appeared. Dismissing only *pinned* toasts fixed that but left the transient errors behind,
// so a red "Calculation Error" sat over correct results for the rest of its five seconds.
// Type is the criterion that separates them: an error reports an attempt, which a new attempt
// supersedes, while a success reports something that actually happened and stays true.
export const dismissFailureToast = () => {
  window.dispatchEvent(new CustomEvent("app-toast-dismiss"))
}

/**
 * Which live region a toast of each type is announced through. An error interrupts, because
 * it reports that something the user asked for did not happen; a success or an info message
 * waits for the screen reader to finish what it is saying.
 */
const LIVE_REGION_BY_TYPE: Readonly<Record<ToastEvent["type"], "alert" | "status">> = {
  error: "alert",
  success: "status",
  info: "status",
}

/** Invisible text alternately appended to a toast's title and message - see showCount. */
const ANNOUNCEMENT_VARIANT = " "

/**
 * Sends focus back to where it was before the user entered the toast, if the toast is holding
 * focus as it goes. Removing the focused button otherwise drops focus to <body>, returning a
 * keyboard user who pressed "Try again" or "Close" to the top of the page. Does nothing when
 * focus is elsewhere, or when it entered from nowhere to return to - <body> is then the only
 * answer anyway.
 */
const returnFocusFromToast = (
  toastRoot: HTMLElement | null,
  focusOrigin: HTMLElement | null
): void => {
  if (toastRoot === null || !toastRoot.contains(document.activeElement)) {
    return
  }
  if (focusOrigin?.isConnected) {
    focusOrigin.focus()
  }
}

const AppToast: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false)
  const [toast, setToast] = useState<ToastEvent | null>(null)
  // AIDEV-NOTE: (#1254 review) Counts shows, for two jobs that each answer the same defect: a
  // toast identical to the one before it - the same "Please fix 1 error" from pressing
  // Calculate twice - was not announced the second time.
  //
  // It keys the toast's DOM, so every show mounts fresh nodes. Before that, an identical repeat
  // changed no node at all (measured with a MutationObserver in Chrome), and re-inserting the
  // node is MDN's documented way to get a repeat announced.
  //
  // That alone was not enough for VoiceOver in Chrome, which was measured speaking only text
  // that differs from what it last read in the region: a remounted identical toast, and one
  // re-shown after 600ms with the region empty, were both silent, and a new message under an
  // unchanged title was read without the title. So the parity of the count also decides
  // whether the title and message carry a trailing non-breaking space (ANNOUNCEMENT_VARIANT),
  // making consecutive toasts differ in text without differing on screen.
  const [showCount, setShowCount] = useState(0)
  const toastRootRef = React.useRef<HTMLDivElement | null>(null)
  // Where focus came from when it last entered the toast, so it can be returned there.
  const focusOriginRef = React.useRef<HTMLElement | null>(null)
  const autoHideDuration = 5000
  const timerRef = React.useRef<number | null>(null)
  // The same value as `toast`, readable from the listeners below. They are attached once, so
  // the `toast` binding they close over is whatever was on screen when the effect ran - which
  // is null on the first pass, and stale on every dismiss after a second toast.
  const currentToastRef = React.useRef<ToastEvent | null>(null)

  // Handle visibility changes
  useEffect(() => {
    // Clear the existing timeout to avoid memory leaks
    const clearToastTimer = () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
    }

    // Start a new timer for auto-hiding the toast
    const startToastTimer = () => {
      clearToastTimer() // Clear any existing timer first
      timerRef.current = window.setTimeout(() => {
        returnFocusFromToast(toastRootRef.current, focusOriginRef.current)
        setIsVisible(false)
      }, autoHideDuration)
    }

    const handleToastEvent = (event: Event) => {
      const customEvent = event as CustomEvent<ToastEvent>
      // The replacement is a remount (see showCount), so a toast being replaced while focused
      // loses focus exactly as a closed one does.
      returnFocusFromToast(toastRootRef.current, focusOriginRef.current)
      currentToastRef.current = customEvent.detail
      setToast(customEvent.detail)
      setShowCount(count => count + 1)
      setIsVisible(true)

      // AIDEV-NOTE: (#991) A toast carrying an action does not auto-hide. The five seconds
      // below are ample for a message that only has to be read, but they are also long
      // enough to miss: a user who looks away returns to find the recovery they were
      // offered has removed itself, and the only way back is to re-submit the form by hand.
      // The previous toast's timer is cleared either way, so a pinned toast replacing a
      // transient one does not inherit its deadline and vanish early.
      clearToastTimer()
      if (customEvent.detail.action === undefined) {
        startToastTimer()
      }
    }

    // The same disposition as the close button's, reachable without a pointer. Deliberately
    // does not clear `toast` itself: hiding is what the close button does too, and keeping
    // the last payload means the two paths cannot diverge in what they leave behind.
    const handleDismissEvent = () => {
      if (currentToastRef.current?.type !== "error") {
        return
      }
      // A transient error reaches here with a live deadline, so the timer is cancelled rather
      // than left to fire against a toast that is already gone.
      clearToastTimer()
      returnFocusFromToast(toastRootRef.current, focusOriginRef.current)
      setIsVisible(false)
    }

    window.addEventListener("app-toast", handleToastEvent)
    window.addEventListener("app-toast-dismiss", handleDismissEvent)

    // Clean up event listeners and timer on unmount
    return () => {
      window.removeEventListener("app-toast", handleToastEvent)
      window.removeEventListener("app-toast-dismiss", handleDismissEvent)
      clearToastTimer()
    }
  }, [autoHideDuration])

  const handleCloseClick = () => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
    returnFocusFromToast(toastRootRef.current, focusOriginRef.current)
    setIsVisible(false)
  }

  // On each button rather than the toast's root, which is not itself interactive.
  const handleButtonFocus = (event: React.FocusEvent<HTMLButtonElement>) => {
    const from = event.relatedTarget
    // Moves between the toast's own buttons are not an entry, and must not overwrite the origin.
    if (from instanceof HTMLElement && !toastRootRef.current?.contains(from)) {
      focusOriginRef.current = from
    }
  }

  // Dismisses first, then runs the action: the action re-runs the work that failed, and
  // leaving its own failure toast to be replaced by a still-open one from the previous
  // attempt would show the user a stale message about an attempt already superseded.
  const handleActionClick = () => {
    const action = toast?.action
    handleCloseClick()
    action?.onAction()
  }

  // Determine background color based on toast type
  const getBgColor = (type: ToastEvent["type"]) => {
    switch (type) {
      case "error":
        return "border-danger"
      case "success":
        return "border-success"
      case "info":
        return "border-info"
      default:
        return "border-primary"
    }
  }

  // Determine icon based on toast type
  const getIcon = (type: ToastEvent["type"]) => {
    switch (type) {
      case "error":
        return "fas fa-exclamation-circle"
      case "success":
        return "fas fa-check-circle"
      case "info":
        return "fas fa-info-circle"
      default:
        return "fas fa-bell"
    }
  }

  // Determine icon color based on toast type
  const getIconColor = (type: ToastEvent["type"]) => {
    switch (type) {
      case "error":
        return "text-danger"
      case "success":
        return "text-success"
      case "info":
        return "text-info"
      default:
        return "text-primary"
    }
  }

  const shownToast = isVisible ? toast : null
  const variant = showCount % 2 === 0 ? ANNOUNCEMENT_VARIANT : ""
  const body =
    shownToast === null ? null : (
      <div
        className={`form-error-toast ${getBgColor(shownToast.type)}`}
        key={showCount}
        ref={toastRootRef}
      >
        <div className="form-error-toast-icon">
          <i
            aria-hidden="true"
            className={`${getIcon(shownToast.type)} ${getIconColor(shownToast.type)}`}
          ></i>
        </div>
        <div className="form-error-toast-content">
          <div className="form-error-toast-title">
            {shownToast.title}
            {variant}
          </div>
          <div className="form-error-toast-message">
            {shownToast.message}
            {variant}
          </div>
          {shownToast.action && (
            <button
              // Bootstrap utilities rather than a rule in _form-error-components.scss: the
              // button needs only spacing above the message, and everything else it wants is
              // what `btn btn-sm btn-outline-secondary` already is.
              className="btn btn-sm btn-outline-secondary mt-2"
              onClick={handleActionClick}
              onFocus={handleButtonFocus}
              type="button"
            >
              {shownToast.action.label}
            </button>
          )}
        </div>
        <button
          className="form-error-toast-close"
          onClick={handleCloseClick}
          onFocus={handleButtonFocus}
          aria-label="Close"
          type="button"
        >
          <i aria-hidden="true" className="fas fa-times"></i>
        </button>
      </div>
    )
  const region = shownToast === null ? null : LIVE_REGION_BY_TYPE[shownToast.type]

  // AIDEV-NOTE: (#1254) Both live regions are rendered unconditionally and only their contents
  // come and go. A screen reader announces a *change* inside a region it is already tracking; a
  // region inserted with its text already in it is often not announced at all, which is how
  // every toast here went unannounced while the whole tree mounted on show.
  //
  // Focus is deliberately left where it is when a toast arrives, including one carrying an
  // action. These toasts arrive when a request settles, seconds after the click that started
  // it, by which time the user may be typing elsewhere - moving focus then would send their
  // keystrokes into the toast, which is why the WAI-ARIA alert pattern leaves focus alone.
  // A screen-reader user reaches the action through the labelled "Notifications" landmark,
  // which is one landmark-navigation keystroke from anywhere. Tab order is a weaker route: App
  // renders this component first, so its buttons are the page's first tab stops, but a user
  // who has just pressed Calculate is deep in the form and Tab moves them away from it. Leaving
  // the toast returns focus to where they entered from (returnFocusFromToast).
  return (
    <section className="form-error-toast-container" aria-label="Notifications">
      <div role="alert" aria-live="assertive" aria-atomic="true">
        {region === "alert" ? body : null}
      </div>
      <div role="status" aria-live="polite" aria-atomic="true">
        {region === "status" ? body : null}
      </div>
    </section>
  )
}

export default AppToast
